import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";
import { hexToRgba } from "../components/ColorPicker";
import { canPreview } from "../lib/filePreview";
import { format, parseISO } from "date-fns";
import { it as itLocale, enUS as enLocale } from "date-fns/locale";

import { ProjectHeader } from "./project/ProjectHeader";
import { ProjectStatusBar } from "./project/ProjectStatusBar";
import { UpdateForm } from "./project/UpdateForm";
import { Timeline } from "./project/Timeline";
import { FileSection } from "./project/FileSection";
import { MeetingSection } from "./project/MeetingSection";
import { FilePreviewModal } from "./project/FilePreviewModal";
import { MeetingDialog } from "./project/MeetingDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_COLOR = "#3B82F6";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const updateFileInputRef = useRef(null);
  const { t, language } = useLanguage();
  const s = localStrings(language);

  const [project, setProject] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [files, setFiles] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUpdate, setNewUpdate] = useState("");
  const [updateFile, setUpdateFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  // Preview state
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Meeting dialog state
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [meetingTitle, setMeetingTitle] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingStartTime, setMeetingStartTime] = useState("");
  const [meetingEndTime, setMeetingEndTime] = useState("");
  const [savingMeeting, setSavingMeeting] = useState(false);

  // Calendar state
  const [selectedDate, setSelectedDate] = useState(new Date());

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, updatesRes, filesRes, meetingsRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`, { withCredentials: true }),
        axios.get(`${API}/projects/${id}/updates`, { withCredentials: true }),
        axios.get(`${API}/projects/${id}/files`, { withCredentials: true }),
        axios.get(`${API}/projects/${id}/meetings`, { withCredentials: true }),
      ]);
      setProject(projectRes.data);
      setUpdates(updatesRes.data);
      setFiles(filesRes.data);
      setMeetings(meetingsRes.data);
    } catch {
      toast.error(localStrings(language)("err.projectLoad"));
      navigate("/projects");
      return;
    } finally {
      setLoading(false);
    }

    // Reading the project is reading its notifications. Asking somebody to
    // then dismiss them separately invents a second chore out of a task they
    // have already done.
    axios
      .post(`${API}/notifications/read/project/${id}`, {}, { withCredentials: true })
      .catch(() => { /* the bell corrects itself on its next poll */ });

    // The directory endpoint returns names and colours only, and is open to
    // every user - unlike /users, which is admin-only. Fetched separately and
    // allowed to fail so a problem here leaves the picker empty rather than
    // taking the whole project page down.
    try {
      const usersRes = await axios.get(`${API}/users/directory`, { withCredentials: true });
      setAllUsers(usersRes.data || []);
    } catch {
      setAllUsers([]);
    }
  }, [id, navigate, language]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- Handlers ---

  const patchProject = async (payload) => {
    const { data } = await axios.patch(`${API}/projects/${id}`, payload, { withCredentials: true });
    setProject(data);
    return data;
  };

  const handleSaveProject = async (payload) => {
    const statusChanged = payload.status && payload.status !== project.status;
    try {
      await patchProject(payload);
      // A status change writes a system entry on the timeline, so reload it.
      if (statusChanged) {
        const { data } = await axios.get(`${API}/projects/${id}/updates`, { withCredentials: true });
        setUpdates(data);
      }
      toast.success(s("ok.projectSaved"));
    } catch {
      toast.error(s("err.projectSave"));
      throw new Error("save failed");
    }
  };

  const handleTogglePerson = async (userId) => {
    const current = (project.people || []).map((p) => p.id);
    const key = String(userId);
    const next = current.includes(key)
      ? current.filter((p) => p !== key)
      : [...current, key];
    try {
      await patchProject({ people: next });
      toast.success(s("ok.peopleSaved"));
    } catch { toast.error(s("err.peopleSave")); }
  };

  const handleSaveWaiting = async (text) => {
    try {
      // An empty string clears the note.
      await patchProject({ waiting_for: text || null });
      toast.success(text ? s("ok.waitingSaved") : s("ok.waitingCleared"));
    } catch { toast.error(s("err.waitingSave")); }
  };

  const handleAddUpdate = async (e) => {
    e.preventDefault();
    if (!newUpdate.trim()) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("content", newUpdate);
      if (updateFile) formData.append("file", updateFile);
      const { data } = await axios.post(`${API}/projects/${id}/updates`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setUpdates([data, ...updates]);
      const hadFile = Boolean(updateFile);
      setNewUpdate(""); setUpdateFile(null);
      // Writing here resets the silence clock, so the header figure is stale.
      try {
        const fresh = await axios.get(`${API}/projects/${id}`, { withCredentials: true });
        setProject(fresh.data);
      } catch { /* the update itself succeeded; a stale counter can wait */ }
      if (hadFile) {
        try {
          const filesRes = await axios.get(`${API}/projects/${id}/files`, { withCredentials: true });
          setFiles(filesRes.data);
        } catch { /* the attachment is on the update either way */ }
      }
      toast.success(s("ok.updatePosted"));
    } catch { toast.error(s("err.updatePost")); }
    finally { setSubmitting(false); }
  };

  /**
   * Correct the text of an update. The author's own, and nobody is told.
   *
   * What comes back is the stored document, so it is merged into the one row
   * rather than refetching the timeline - the card then shows exactly what was
   * saved and nothing else on the page moves. The project is not refetched
   * either: posting an update resets the silence clock and this does not touch
   * created_at, so no figure in the header has gone stale.
   *
   * Returns whether it worked, so the card can stay open with the draft still
   * in it when it did not.
   */
  const handleEditUpdate = async (updateId, content) => {
    try {
      const { data } = await axios.patch(
        `${API}/projects/${id}/updates/${updateId}`,
        { content },
        { withCredentials: true }
      );
      setUpdates((prev) => prev.map((u) => (u.id === updateId ? { ...u, ...data } : u)));
      toast.success(t("project.updateEdited"));
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || t("project.updateEditFailed"));
      return false;
    }
  };

  const handleFileUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", fileList[0]);
    try {
      const { data } = await axios.post(`${API}/projects/${id}/files`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setFiles([data, ...files]);
      toast.success(s("ok.fileUploaded"));
    } catch { toast.error(s("err.fileUpload")); }
    finally { setUploading(false); }
  };

  const handleFileDelete = async (fileId) => {
    try {
      await axios.delete(`${API}/files/${fileId}`, { withCredentials: true });
      setFiles(files.filter((f) => f.id !== fileId));
      toast.success(s("ok.fileDeleted"));
    } catch { toast.error(s("err.fileDelete")); }
  };

  const handleDownload = async (file) => {
    try {
      const response = await axios.get(`${API}/files/${file.id}/download`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error(s("err.fileDownload")); }
  };

  // The blob is built here with the stored content type, so the server's
  // Content-Disposition, type and policy never enter into it - which is why the
  // previewable table in lib/filePreview.js, not the _INLINE_TYPES list in
  // files.py, is what keeps a scriptable type out of the dialog on this path.
  const handlePreview = async (file) => {
    if (!canPreview(file.content_type)) { handleDownload(file); return; }
    setPreviewFile(file);
    setPreviewLoading(true);
    try {
      const response = await axios.get(`${API}/files/${file.id}/download?preview=true`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: file.content_type }));
      setPreviewUrl(url);
    } catch { toast.error(s("err.filePreview")); setPreviewFile(null); }
    finally { setPreviewLoading(false); }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewFile(null); setPreviewUrl(null);
  };

  const handleDeleteProject = async () => {
    try {
      await axios.delete(`${API}/projects/${id}`, { withCredentials: true });
      toast.success(s("ok.projectDeleted"));
      navigate("/projects");
    } catch { toast.error(s("err.projectDelete")); }
  };

  const handleDeleteUpdate = async (updateId) => {
    try {
      await axios.delete(`${API}/projects/${id}/updates/${updateId}`, { withCredentials: true });
      setUpdates(updates.filter((u) => u.id !== updateId));
      toast.success(s("ok.updateDeleted"));
    } catch { toast.error(s("err.updateDelete")); }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    if (!meetingTitle || !meetingDate || !meetingStartTime || !meetingEndTime) return;
    setSavingMeeting(true);
    try {
      const { data } = await axios.post(`${API}/projects/${id}/meetings`, { title: meetingTitle, date: meetingDate, start_time: meetingStartTime, end_time: meetingEndTime }, { withCredentials: true });
      setMeetings([...meetings, data].sort((a, b) => a.date.localeCompare(b.date)));
      if (data.timeline_update) setUpdates([data.timeline_update, ...updates]);
      setMeetingDialogOpen(false);
      setMeetingTitle(""); setMeetingDate(""); setMeetingStartTime(""); setMeetingEndTime("");
      toast.success(s("ok.meetingCreated"));
    } catch { toast.error(s("err.meetingCreate")); }
    finally { setSavingMeeting(false); }
  };

  const handleDeleteMeeting = async (meetingId) => {
    try {
      await axios.delete(`${API}/meetings/${meetingId}`, { withCredentials: true });
      setMeetings(meetings.filter((m) => m.id !== meetingId));
      setUpdates(updates.filter((u) => u.meeting_id !== meetingId));
      toast.success(s("ok.meetingDeleted"));
    } catch { toast.error(s("err.meetingDelete")); }
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragging(false); };
  const handleDrop = (e) => { e.preventDefault(); setDragging(false); handleFileUpload(e.dataTransfer.files); };

  // The date on every card of the timeline. It carried an English pattern and
  // no locale, so an Italian page dated each update "Sep 14, 2026 at 3:04 PM".
  // The same correction was made in Feed, NotificationBell, NewsWidget and
  // WeekCalendar; this one was written before them and was missed.
  const formatDate = (dateString) => {
    try {
      return format(
        new Date(dateString),
        language === "it" ? "d MMM yyyy, HH:mm" : "MMM d, yyyy, h:mm a",
        { locale: language === "it" ? itLocale : enLocale }
      );
    } catch { return dateString; }
  };

  const meetingDates = meetings.map(m => { try { return parseISO(m.date); } catch { return null; } }).filter(Boolean);

  // --- Loading / Empty ---
  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  if (!project) return null;

  const accent = project.color || DEFAULT_COLOR;

  return (
    <div
      className="min-h-screen"
      style={{
        // The project's colour bleeding down from the top of the page. Far
        // weaker than the header panel and gone entirely by 40% down, so the
        // page feels like this project without any surface below it having to
        // change.
        background: `linear-gradient(to bottom, ${hexToRgba(accent, 0.1)} 0%, ${hexToRgba(accent, 0.03)} 18%, rgba(255,255,255,0) 40%)`,
      }}
    >
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Link to="/projects" data-testid="back-to-projects" className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("project.backToProjects")}
          </Link>

          <ProjectHeader
            project={project}
            user={user}
            t={t}
            s={s}
            onSaveProject={handleSaveProject}
            handleDeleteProject={handleDeleteProject}
          />

          {/* State before story: how long it has been quiet, who the reference
              people are, and what it is waiting on. */}
          <ProjectStatusBar
            project={project}
            allUsers={allUsers}
            onSaveWaiting={handleSaveWaiting}
            onTogglePerson={handleTogglePerson}
            s={s}
          />

          {/* grid-cols-1 rather than no columns at all on a phone. An implicit
              column is sized to its content, so one long file name - truncated,
              but never wrapped - widened the column and every card in it past
              the edge of the screen. A declared column can shrink to the screen,
              and the name is then cut where it was always meant to be. */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 min-w-0">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{t("project.updates")}</h2>
              <UpdateForm
                newUpdate={newUpdate} setNewUpdate={setNewUpdate}
                updateFile={updateFile} setUpdateFile={setUpdateFile}
                handleAddUpdate={handleAddUpdate} submitting={submitting}
                updateFileInputRef={updateFileInputRef} t={t}
              />
              <Timeline
                updates={updates} handleDeleteUpdate={handleDeleteUpdate}
                handleEditUpdate={handleEditUpdate}
                handlePreview={handlePreview} handleDownload={handleDownload}
                user={user} t={t} formatDate={formatDate}
              />
            </div>

            <div className="lg:col-span-4 space-y-8 min-w-0">
              <FileSection
                files={files} fileInputRef={fileInputRef}
                handleFileUpload={handleFileUpload} handleFileDelete={handleFileDelete}
                handleDownload={handleDownload} handlePreview={handlePreview}
                uploading={uploading} dragging={dragging}
                handleDragOver={handleDragOver} handleDragLeave={handleDragLeave}
                handleDrop={handleDrop} t={t}
              />
              <MeetingSection
                meetings={meetings} selectedDate={selectedDate}
                setSelectedDate={setSelectedDate} meetingDates={meetingDates}
                setMeetingDialogOpen={setMeetingDialogOpen}
                handleDeleteMeeting={handleDeleteMeeting} t={t}
              />
            </div>
          </div>
        </motion.div>
      </main>

      <FilePreviewModal
        previewFile={previewFile} previewUrl={previewUrl}
        previewLoading={previewLoading} closePreview={closePreview}
        handleDownload={handleDownload}
      />

      <MeetingDialog
        open={meetingDialogOpen} onOpenChange={setMeetingDialogOpen}
        meetingTitle={meetingTitle} setMeetingTitle={setMeetingTitle}
        meetingDate={meetingDate} setMeetingDate={setMeetingDate}
        meetingStartTime={meetingStartTime} setMeetingStartTime={setMeetingStartTime}
        meetingEndTime={meetingEndTime} setMeetingEndTime={setMeetingEndTime}
        savingMeeting={savingMeeting} handleCreateMeeting={handleCreateMeeting} t={t}
      />
    </div>
  );
}
